import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Briefcase, Globe2 } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import { MaskedLine, FadeIn, Reveal } from "@/components/Reveal";
import EditorialMarquee from "@/components/Marquee";
import { IMAGES, LINKS, BOOK_COVER } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const WORLDS = [
  {
    icon: Compass,
    area: "Travel",
    title: "Scopri il mondo.",
    text: "Per chi ama viaggiare, conoscere nuove destinazioni e vivere esperienze. Scopri il mondo TravelAdvantage e le possibilità offerte da un club internazionale di viaggiatori.",
    cta: "Scopri il mondo Travel",
    to: "/travel-advantage",
    image: IMAGES.santorini,
    pos: "center 52%",
    testid: "world-travel",
  },
  {
    icon: Briefcase,
    area: "Business",
    title: "Costruisci il tuo percorso.",
    text: "Per chi desidera conoscere il networking, sviluppare un'attività nel settore dei viaggi e costruire nuove possibilità professionali, anche accanto al proprio lavoro.",
    cta: "Scopri il business",
    to: "/un-nuovo-modo-di-lavorare",
    image: IMAGES.mwrEvento,
    testid: "world-business",
  },
  {
    icon: Globe2,
    area: "International Connections",
    title: "Le connessioni aprono nuove strade.",
    text: "Relazioni internazionali, imprenditori, collaborazioni e progetti che mettono in connessione persone e realtà diverse.",
    cta: "Scopri le collaborazioni",
    anchor: "visione",
    image: IMAGES.kruja,
    pos: "center 45%",
    testid: "world-international",
  },
];

export default function Home() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data.slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Laure Hila | Viaggia in Grande – Viaggi, Business e Networking"
        description="Scopri il mondo di Laure Hila: viaggi, MWR Life, imprenditoria, networking e collaborazioni internazionali. Nuove possibilità per crescere e costruire."
      />

      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[100svh] flex items-center grain overflow-hidden pt-24 md:pt-28 pb-14"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <FadeIn delay={0.15}>
              <p className="eyebrow mb-5" data-testid="hero-eyebrow">
                Viaggia in Grande
              </p>
            </FadeIn>
            <h1 className="font-serif font-semibold text-ink tracking-tight leading-none">
              <MaskedLine delay={0.25} className="text-[1.6rem] sm:text-[2.15rem] lg:text-[2.7rem]">
                LAURE <span className="italic text-gold">HILA</span>
              </MaskedLine>
            </h1>
            <h2 className="font-serif text-ink mt-6 leading-[1.08] tracking-tight">
              <MaskedLine delay={0.45} className="text-[9.5vw] sm:text-5xl lg:text-[4.2rem] font-medium">
                Il mondo offre più possibilità
              </MaskedLine>
              <MaskedLine delay={0.6} className="text-[9.5vw] sm:text-5xl lg:text-[4.2rem] font-medium">
                di quelle che <span className="italic text-gold">immagini</span>.
              </MaskedLine>
            </h2>
            <FadeIn delay={0.85}>
              <p className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-stone">
                Ho scelto di cambiare direzione, costruire qualcosa di mio e
                aprirmi a nuove opportunità attraverso il mondo dei viaggi,
                dell'imprenditoria e delle connessioni internazionali. Oggi voglio
                condividere questa visione con persone che desiderano crescere,
                mettersi in gioco e scoprire nuove possibilità.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-4">
                <Link
                  to="/la-mia-storia"
                  data-testid="hero-cta-storia"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold transition-colors duration-300 group"
                >
                  Scopri la mia storia
                  <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <button
                  onClick={() => scrollTo("tre-mondi")}
                  data-testid="hero-cta-opportunita"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-ink/25 text-ink font-semibold text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  Esplora le opportunità
                </button>
                <a
                  href={LINKS.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold tracking-wide text-ink/70 hover:text-gold transition-colors duration-300 link-underline"
                >
                  Parliamo su WhatsApp
                </a>
              </div>
            </FadeIn>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <FadeIn delay={0.35}>
              <div className="frame-gold max-w-[340px] sm:max-w-[420px] mx-auto lg:ml-auto">
                <div className="relative z-10 overflow-hidden rounded-xl">
                  <img
                    src={IMAGES.proPontile.url}
                    alt={IMAGES.proPontile.alt}
                    data-testid="hero-photo"
                    style={{ objectPosition: "center 35%" }}
                    className="w-full aspect-[3/4] object-cover"
                    loading="eager"
                  />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* LA MIA STORIA */}
      <section data-testid="home-storia" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <Reveal className="md:col-span-5">
            <div className="frame-gold">
              <div className="relative z-10 overflow-hidden rounded-xl">
                <img
                  src={IMAGES.proCappotto.url}
                  alt={IMAGES.proCappotto.alt}
                  style={{ objectPosition: "center 16%" }}
                  className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">La mia storia</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink text-balance">
                Non è mai troppo tardi per{" "}
                <span className="italic text-gold">cambiare direzione</span>.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-stone">
                Per oltre 25 anni ho seguito un percorso lavorativo tradizionale.
                Poi ho capito che desideravo costruire qualcosa anche per me,
                sviluppare nuove capacità e aprirmi a possibilità che prima non
                avevo considerato.
              </p>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-stone">
                Oggi il mio percorso unisce imprenditoria, viaggi, networking e
                relazioni internazionali.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/la-mia-storia"
                data-testid="storia-cta"
                className="mt-8 inline-flex items-center gap-2 text-gold font-semibold text-sm tracking-wide link-underline group"
              >
                Conosci la mia storia
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRE MONDI */}
      <section id="tre-mondi" data-testid="home-tre-mondi" className="py-20 md:py-28 bg-sand/60 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-12 md:mb-16">
            <p className="eyebrow mb-4">Cosa puoi scoprire qui</p>
            <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink">
              Tre mondi, <span className="italic text-gold">una sola visione</span>.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {WORLDS.map((w, i) => (
              <Reveal key={w.area} delay={i * 0.1} className="h-full">
                <div
                  data-testid={w.testid}
                  className="h-full flex flex-col rounded-2xl bg-cream border border-line hover:border-gold transition-colors duration-500 overflow-hidden group"
                >
                  <div className="overflow-hidden">
                    <img
                      src={w.image.url}
                      alt={w.image.alt}
                      style={{ objectPosition: w.pos || "center" }}
                      className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-7 md:p-8 flex flex-col flex-1">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold flex items-center gap-2">
                      <w.icon size={15} strokeWidth={1.8} />
                      {w.area}
                    </p>
                    <h3 className="mt-3 font-serif text-2xl md:text-[1.7rem] text-ink leading-snug">
                      {w.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base leading-relaxed text-stone flex-1">
                      {w.text}
                    </p>
                    {w.to ? (
                      <Link
                        to={w.to}
                        data-testid={`${w.testid}-cta`}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-gold transition-colors duration-300 link-underline w-fit group/btn"
                      >
                        {w.cta}
                        <ArrowRight size={15} className="group-hover/btn:translate-x-1 transition-transform duration-300" />
                      </Link>
                    ) : (
                      <button
                        onClick={() => scrollTo(w.anchor)}
                        data-testid={`${w.testid}-cta`}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-gold transition-colors duration-300 link-underline w-fit group/btn"
                      >
                        {w.cta}
                        <ArrowRight size={15} className="group-hover/btn:translate-x-1 transition-transform duration-300" />
                      </button>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OLTRE IL VIAGGIO */}
      <section data-testid="home-oltre" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="eyebrow mb-4">Oltre il viaggio</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink text-balance">
                Non solo destinazioni.{" "}
                <span className="italic text-gold">Persone, formazione e opportunità</span>.
              </h2>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-stone">
                Credo nel valore delle relazioni, nella formazione continua e
                nella possibilità di crescere insieme. Attraverso il networking,
                gli incontri, gli eventi e le connessioni internazionali possiamo
                conoscere nuove realtà, condividere esperienze e sviluppare nuove
                prospettive.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6 mt-12 md:mt-16">
            {[
              { img: IMAGES.mwrConvention, label: "Formazione e convention", pos: "center 35%" },
              { img: IMAGES.mwrPalco, label: "Eventi internazionali", pos: "center 45%" },
              { img: IMAGES.mwrCommunity, label: "Community e relazioni", pos: "center" },
            ].map((item, i) => (
              <Reveal key={item.label} delay={i * 0.1}>
                <figure className="group">
                  <div className="overflow-hidden rounded-xl">
                    <img
                      src={item.img.url}
                      alt={item.img.alt}
                      style={{ objectPosition: item.pos }}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                  </div>
                  <figcaption className="mt-3 text-xs uppercase tracking-[0.18em] text-stone">
                    {item.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISIONE INTERNAZIONALE */}
      <section id="visione" data-testid="home-visione" className="py-20 md:py-28 bg-ink text-cream grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="eyebrow mb-4">La mia visione internazionale</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-balance">
                Le opportunità <span className="italic text-gold">non hanno confini</span>.
              </h2>
              <p className="mt-7 text-base md:text-lg leading-relaxed text-cream/70">
                Il mio percorso personale e professionale mi ha portata a guardare
                oltre i confini, creando connessioni tra Italia, Albania e il
                mondo. Credo nel potenziale delle persone, nella collaborazione
                tra imprenditori e nello sviluppo di progetti capaci di creare
                nuove relazioni internazionali.
              </p>
              <Link
                to="/skal-albania"
                data-testid="visione-cta-skal"
                className="mt-8 inline-flex items-center gap-2 text-gold font-semibold text-sm tracking-wide link-underline group"
              >
                Scopri il progetto Skål International Albania
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.12} className="md:col-span-5">
            <div className="overflow-hidden rounded-xl">
              <img
                src={IMAGES.scutari.url}
                alt={IMAGES.scutari.alt}
                style={{ objectPosition: "center 62%" }}
                className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* LIBRO TEASER */}
      <section data-testid="home-libro" className="py-20 md:py-28 grain relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <Reveal className="md:col-span-4 md:col-start-2">
            <div className="relative mx-auto max-w-[260px]">
              <div className="absolute -inset-4 bg-gold/10 rounded-2xl rotate-3" />
              <img
                src={BOOK_COVER.url}
                alt={BOOK_COVER.alt}
                className="relative z-10 w-full rounded-lg shadow-[0_30px_60px_rgba(44,42,41,0.25)]"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div className="md:col-span-6">
            <Reveal>
              <p className="eyebrow mb-4">Il mio libro</p>
              <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink">
                Io e il mio <span className="italic text-gold">18</span>
              </h2>
              <p className="mt-3 font-serif text-xl italic text-stone">
                La storia di mia figlia Antonella
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-stone max-w-lg">
                Ho scritto queste pagine perché il nome di Antonella non venisse
                dimenticato. Una storia di amore incondizionato, coraggio e fede.
              </p>
              <Link
                to="/libro"
                data-testid="libro-teaser-cta"
                className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold transition-colors duration-300 group"
              >
                Scopri il libro
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BLOG TEASER */}
      {posts.length > 0 && (
        <section data-testid="home-blog" className="py-20 md:py-28 bg-sand/60 grain relative">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
              <Reveal>
                <p className="eyebrow mb-4">Dal blog</p>
                <h2 className="font-serif text-4xl md:text-5xl font-medium tracking-tight text-ink">
                  Pensieri in <span className="italic text-gold">viaggio</span>
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <Link
                  to="/blog"
                  data-testid="blog-teaser-cta"
                  className="inline-flex items-center gap-2 text-ink font-semibold text-sm tracking-wide link-underline group w-fit"
                >
                  Tutti gli articoli
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.1}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`home-post-${p.slug}`}
                    className="group block"
                  >
                    <div className="overflow-hidden rounded-xl">
                      <img
                        src={p.cover_image}
                        alt={p.cover_alt}
                        style={{ objectPosition: p.cover_position || "center" }}
                        className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-5 text-xs uppercase tracking-[0.18em] text-gold font-semibold">
                      {p.category}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl leading-snug text-ink group-hover:text-gold transition-colors duration-300">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-stone line-clamp-2">
                      {p.excerpt}
                    </p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINALE */}
      <section data-testid="home-cta-finale" className="py-24 md:py-36 grain relative">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="eyebrow mb-4">Una visione che continua a crescere</p>
            <h2 className="font-serif text-3xl md:text-5xl font-medium tracking-tight text-ink text-balance leading-snug">
              «Non devi avere già tutte le risposte per iniziare. A volte basta il
              coraggio di fare <span className="italic text-gold">il primo passo</span>.»
            </h2>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contatti"
                data-testid="cta-finale-contatti"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold transition-colors duration-300"
              >
                Parliamone
              </Link>
              <Link
                to="/un-nuovo-modo-di-lavorare"
                data-testid="cta-finale-percorso"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-ink/25 text-ink font-semibold text-sm tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
              >
                Scopri il mio percorso
              </Link>
              <a
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-finale-whatsapp"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold tracking-wide text-ink/70 hover:text-gold transition-colors duration-300 link-underline"
              >
                Scrivimi su WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
